<i18n>
{
  "ja": {
    "warehouse-at": "{address}の倉庫",
    "address" : "所在地",
    "location":"立地",
    "min-from-location": "{locationName}から{min}分",
    "comma-space": "、",
    "contact-warehouse": "この倉庫に問い合わせ",
    "weekdays": "月〜金",
    "saturdays": "土曜",
    "sundays": "日曜",
    "holidays": "祝日",
    "delivery": {
      "self" : "自社便",
      "self" : "チャーター便",
      "self" : "路線便"
    }
  }
}
</i18n>
<template>
  <div
    class="w-full warehouse flex flex-col my-12 text-left"
    :class="`warehouse-id-${warehouse.id} code-${warehouse.display_number}`"
  >
    <h2 class="flex-1 text-2xl leading-tight mb-2 md:hidden">
      <nuxt-link :to="{ path: localePath(`/warehouses/${warehouse.display_number}`), query: {...$route.query, area: $route.params.area }}" class="text-secondary font-bold underline">
        {{ $t('warehouse-at', { address })}}
      </nuxt-link>
    </h2>
    <div class="flex-1 flex flex-col md:flex-row md:items-start">
      <nuxt-link class="warehouse-photo-container" :to="{ path: localePath(`/warehouses/${warehouse.display_number}`), query: {...$route.query, area: $route.params.area }}">
        <v-lazy-image
          :src="getFirstUrl(warehouse.photos)"
          src-placeholder="/img/WareX-chicklet.png"
          class="warehouse-photo"
        />
      </nuxt-link>
      <div class="info flex-1 inline-block mt-6 md:mt-0 text-xs">
        <section class="flex items-between">
          <div class="flex-1">
            <h2 class="text-2xl leading-tight mb-2 hidden md:block">
              <nuxt-link :to="{ path: localePath(`/warehouses/${warehouse.display_number}`), query: {...$route.query, area: $route.params.area }}" class="text-secondary font-bold underline">
                {{ $t('warehouse-at', { address })}}
              </nuxt-link>
            </h2>
            <div class="flex items-center">
              <span class="text-base">{{ address }}</span>
              <nuxt-link
                :to="{ path: localePath(`/warehouses/search/map/${area}/?pin_id=${warehouse.display_number}`) }"
                class="inline-flex items-center h-5 ml-2 link-map bg-secondary text-white px-2 rounded text-xs"
              >
                <span>MAP</span>
              </nuxt-link>
            </div>
          </div>
          <freespace-info v-if="showFreeSpace" :free-spaces="warehouse.free_spaces" :md="true" />
        </section>
        <section class="mt-1 text-xs flex">{{ locations }}</section>
        <section v-if="showPromoTitle" class="mt-1 bg-gray-200 px-3 py-2">
          <div class="promo-title text-xs truncate">{{ warehouse.promo_title }}</div>
        </section>
        <section v-if="hasSupportedCargo" class="mt-3 flex">
          <div class="inline-flex justify-center items-center w-5 h-6 mr-4 flex-shrink-0">
            <img src="~assets/img/warehouse/icon/boxes-stacked.svg" alt="">
          </div>
          <ul class="tags flex flex-wrap">
            <li v-for="(item, index) in warehouse.supported_cargo" :key="index">{{ $t(`cargoType.${item}`) }}</li>
          </ul>
        </section>
        <section class="mt-3 flex">
          <div class="inline-flex justify-center items-center w-5 h-6 mr-4"><img src="~assets/img/warehouse/icon/calendar-clock.svg" alt=""></div>
          <ul class="tags">
            <li :class="{ nope: !warehouse.weekdays }">{{$t('weekdays')}}<span class="ml-2" v-if="warehouse.weekdays">{{ warehouse.weekdays }}</span></li>
            <li :class="{ nope: !warehouse.saturdays }">{{$t('saturdays')}}</li>
            <li :class="{ nope: !warehouse.sundays }">{{$t('sundays')}}</li>
            <li :class="{ nope: !warehouse.holidays }">{{$t('holidays')}}</li>
          </ul>
        </section>
        <section class="mt-3 flex">
          <div class="inline-flex justify-center items-center w-5 h-6 mr-4"><img src="~assets/img/warehouse/icon/truck.svg" alt=""></div>
          <ul class="tags">
            <li v-for="opt in deliveryTypes" :key="opt" :class="{ nope: !warehouse.delivery_types.includes(opt) }">{{$t(`warehouse.${opt}`)}}</li>
          </ul>
        </section>
        <section class="mt-3 flex">
          <div class="inline-flex justify-center items-center w-5 h-6 mr-4"><img src="~assets/img/warehouse/icon/temperature.svg" alt=""></div>
          <ul class="tags">
            <li v-for="opt in temperatureTypes" :key="opt" :class="{ nope: !warehouse.temperature.includes(opt) }">{{$t(`warehouse.${opt}`)}}</li>
          </ul>
        </section>
        <section class="flex flex-col-reverse md:flex-row justify-center items-center md:justify-start mt-8 md:mt-6">
          <nuxt-link
            class="detail-warehouse-link mt-3 md:mt-0"
            :to="{ path: localePath(`/warehouses/${warehouse.display_number}`), query: {...$route.query, area: $route.params.area }}"
          >
            倉庫の詳細を見る
          </nuxt-link>
          <button class="heap-contact-warehouse-list contact-warehouse-link md:ml-6 cursor-pointer h-12 md:h-10" @click="openInquiry(warehouse)">
            この倉庫に問い合わせ
          </button>
        </section>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.warehouse-photo-container
  @screen md
    width: 31.25%
    max-width: 20rem
    flex-shrink: 0
    flex-grow: 0
    padding-right: 2.75rem
.warehouse-photo
  aspect-ratio: 276 / 187
  object-fit: cover
  object-position: 50% 50%
  @apply w-full rounded-lg
.tags
  @apply flex flex-wrap items-center
  > * + *
    margin-top: 0.25rem
  li
    @apply inline-flex items-center h-5 px-2 rounded text-sm bg-gray-200 mr-1
    &.nope
      @apply text-gray-400
.detail-warehouse-link
  @apply inline-flex h-10 justify-center items-center rounded-full text-center text-sm font-bold whitespace-no-wrap text-secondary border border-secondary px-3
  min-width: 14rem
.contact-warehouse-link
  @apply inline-flex h-10 justify-center items-center rounded-full text-center text-sm font-bold whitespace-no-wrap text-white bg-orange px-3
  min-width: 14rem
.contact-warehouse-link-icon
  filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)
  height: 1.25rem
</style>

<script>
import { mapGetters } from 'vuex';
import { getFirstUrl } from '@/components/utils/warehouse';
import { deliveryTypes, temperatureTypes } from '@/model/warehouse';
import { isEmpty } from '@/components/utils/string';
import freespaceInfo from '@/components/warehouses/search/freespaceInfo.vue';

export default {
  components: { freespaceInfo },
  props: {
    warehouse: { type: Object, required: true },
    area: { type: String, required: true }
  },
  data() {
    const dt = deliveryTypes.concat();
    const otherIndex = dt.indexOf('other');
    if (otherIndex >= 0) dt.splice(otherIndex, 1);
    return {
      deliveryTypes : dt,
      temperatureTypes
    }
  },
  computed: {
    ...mapGetters('nav', ['isMobile', 'isDesktop', 'headerHeight']),
    address () {
      return this.warehouse.location;
    },
    locations () {
      return this.warehouse.relative_locations.map((loc) => {
        return this.$t('min-from-location', { locationName: loc.name, min: loc.distance_in_minutes });
      }).join(this.$t('comma-space'));
    },
    showFreeSpace () {
      // Hide for mobile - mobile layout doesn't have enough space.
      // Hide when the user doesn't input the free space in the first place
      return !this.isMobile && this.warehouse.free_spaces.length > 0;
    },
    showPromoTitle () {
      return this.warehouse.promo_title_displayed && !isEmpty(this.warehouse.promo_title);
    },
    hasSupportedCargo () {
      return this.warehouse.supported_cargo.length > 0;
    },
  },
  methods: {
    getFirstUrl,
    openInquiry (warehouse) {
      this.$router.push({ path: this.localePath('/inquiry/'), query: { warehouse_code: warehouse.display_number } });
    }
  }
}
</script>